import React, { useState } from "react";
import { toast } from "sonner";
import { Diamond, Award, ScrollText, Check } from "lucide-react";
import api from "../../lib/api";

const HERO = "/products/prod_legacy.png";

export default function Legacy() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", country: "", engraving: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const change = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/legacy/request", { ...form, request_id: "" });
      setDone(true);
      toast.success("Concierge will reach you within 24 hours");
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="legacy-page" className="bg-poche-black text-white">
      {/* HERO */}
      <section className="relative h-[100vh] min-h-[640px] overflow-hidden">
        <img src={HERO} alt="Legacy edition" className="absolute inset-0 w-full h-full object-cover opacity-90" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/50 to-black/30" />
        <div className="absolute inset-0 grain" />
        <div className="relative container-x h-full flex flex-col justify-end pb-24">
          <p className="text-[11px] tracking-luxury uppercase text-poche-gold fade-up">Joseph Poche</p>
          <h1 className="font-serif text-6xl md:text-8xl mt-4 leading-[0.9] fade-up-d1">
            The Legacy<br />
            <span className="italic text-poche-gold">Edition.</span>
          </h1>
          <p className="text-white/70 mt-6 max-w-xl text-lg leading-relaxed fade-up-d2">
            Limited to 25 pieces worldwide. 18k solid gold. Natural buffalo horn. Hand-set baguette diamonds.
            Each piece engraved with the owner's name. Each piece numbered. Each piece witnessed.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-6 fade-up-d3">
            <div className="font-serif text-5xl text-poche-gold">$10,000</div>
            <div className="text-xs tracking-luxury uppercase text-white/55">Per piece · Concierge purchase only</div>
          </div>
        </div>
      </section>

      {/* Specs grid */}
      <section className="container-x py-24 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <p className="text-[11px] tracking-luxury uppercase text-poche-gold">Details that define legacy</p>
          <h2 className="font-serif text-5xl mt-4 leading-tight">Designed for the<br />ones who build.</h2>
          <p className="mt-6 text-white/65 leading-relaxed">
            The Legacy Edition is not eyewear. It is a statement of identity, craftsmanship, and generational impact.
            Each piece is hand-numbered from 01/25 to 25/25, engraved with your name, and presented in a hand-crafted
            wooden case lined with suede.
          </p>
        </div>
        <div className="md:col-span-7 grid grid-cols-2 gap-px bg-poche-gold/30">
          {[
            { t: "18K Solid Gold", b: "Handcrafted from solid 18k gold. Polished by hand for 19 hours per frame." },
            { t: "Natural Buffalo Horn", b: "Sourced, shaped and polished by master artisans. Unique grain on every piece." },
            { t: "Baguette Diamonds", b: "Hand-set baguette diamonds on the temples. Total 3.6 carats. Color D · Clarity VVS." },
            { t: "ZEISS Mineral Glass", b: "Lenses ground from optical-grade mineral glass. 100% UV protection." },
            { t: "Engraved Plaque", b: "18k gold plaque with edition number 01/25 and JOSEPH POCHE wordmark." },
            { t: "Swiss Atelier", b: "Made in Switzerland. Inspected three times. Witnessed by master atelier." },
          ].map((s, i) => (
            <div key={i} className="bg-poche-ink p-8">
              <div className="font-serif text-2xl text-poche-gold">{s.t}</div>
              <p className="text-sm text-white/60 mt-3 leading-relaxed">{s.b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* What's included */}
      <section className="bg-poche-ink border-t border-poche-gold/15">
        <div className="container-x py-20 grid grid-cols-1 md:grid-cols-2 gap-16 items-start">
          <div>
            <p className="text-[11px] tracking-luxury uppercase text-poche-gold">The Legacy Presentation</p>
            <h2 className="font-serif text-5xl mt-4 leading-tight">Each piece arrives in ceremony.</h2>
          </div>
          <ul className="space-y-4">
            {[
              "Handcrafted Luxury Wooden Box with Gold Details",
              "18K Gold Plaque · Numbered 01/25 to 25/25",
              "Certificate of Authenticity · Signed by Joseph Poche",
              "Premium Italian Leather Travel Case",
              "Microfiber Cloth in Suede Pouch",
              "White Gloves for Handling",
              "Owner's Manual & House Provenance Book",
              "Personal Thank You Letter from Joseph Poche",
            ].map((i) => (
              <li key={i} className="flex items-center gap-4 border-b border-white/10 pb-4">
                <Check size={16} className="text-poche-gold flex-shrink-0" strokeWidth={1.5} />
                <span className="text-white/80">{i}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Concierge form */}
      <section className="container-x py-24 grid grid-cols-1 md:grid-cols-2 gap-16">
        <div>
          <p className="text-[11px] tracking-luxury uppercase text-poche-gold">Concierge Acquisition</p>
          <h2 className="font-serif text-5xl mt-4 leading-tight">Request<br />a private consultation.</h2>
          <p className="text-white/65 mt-6 max-w-md leading-relaxed">
            The Legacy Edition is reserved by appointment. A senior concierge will respond within 24 hours
            to begin your acquisition — including engraving, sizing, and authenticated white-glove delivery.
          </p>
          <div className="mt-10 grid grid-cols-3 gap-6">
            {[
              { i: Diamond, t: "25 Pieces" },
              { i: Award, t: "Numbered" },
              { i: ScrollText, t: "Certificate" },
            ].map((b, i) => (
              <div key={i} className="border-t border-poche-gold/30 pt-5">
                <b.i size={20} className="text-poche-gold" strokeWidth={1.4} />
                <div className="text-[10px] tracking-luxury uppercase text-white/55 mt-3">{b.t}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="glass-dark p-10">
          {done ? (
            <div className="text-center py-16" data-testid="legacy-success">
              <div className="font-serif text-5xl text-poche-gold">Received.</div>
              <p className="text-white/65 mt-4 max-w-sm mx-auto">
                A senior concierge will contact you within 24 hours to begin your acquisition.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5">
              <Field label="Full Name" testid="legacy-name" value={form.name} onChange={change("name")} required />
              <Field label="Email" testid="legacy-email" type="email" value={form.email} onChange={change("email")} required />
              <Field label="Phone" testid="legacy-phone" value={form.phone} onChange={change("phone")} required />
              <Field label="Country" testid="legacy-country" value={form.country} onChange={change("country")} required />
              <Field label="Engraving (your name as it should appear)" testid="legacy-engraving" value={form.engraving} onChange={change("engraving")} required />
              <div>
                <label className="text-[10px] tracking-luxury uppercase text-white/55">Notes</label>
                <textarea data-testid="legacy-notes" value={form.notes} onChange={change("notes")} rows={3} className="w-full bg-transparent border-b border-white/25 focus:border-poche-gold py-3 text-white focus:outline-none" />
              </div>
              <button data-testid="legacy-submit" disabled={submitting} className="btn-gold w-full justify-center disabled:opacity-60 mt-4">
                {submitting ? "Submitting…" : "Request Consultation"}
              </button>
              <p className="text-[10px] tracking-luxury uppercase text-white/35 text-center pt-2">
                A 20% deposit secures your piece · refundable until final engraving.
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid }) {
  return (
    <div>
      <label className="text-[10px] tracking-luxury uppercase text-white/55">{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={onChange} required={required} className="w-full bg-transparent border-b border-white/25 focus:border-poche-gold py-3 text-white focus:outline-none" />
    </div>
  );
}
